'use client';

import { KeyRound, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Button } from '@/app/_components/ui/button';
import { ApiPasskey } from '@/lib/core-api/types';
import {
  createPasskey,
  isPasskeyAlreadyRegistered,
  isPasskeyCancelled,
} from '@/lib/passkeys/client';

import {
  passkeyRegisterOptionsAction,
  passkeyRegisterVerifyAction,
} from './action';

/** Nom d'appareil proposé par défaut, deviné depuis le navigateur (modifiable). */
export const guessDeviceName = (): string => {
  if (typeof navigator === 'undefined') {
    return '';
  }
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/.test(ua)) {
    return 'iPad';
  }
  if (/Android/.test(ua)) {
    return 'Android';
  }
  if (/Macintosh/.test(ua)) {
    return 'Mac';
  }
  if (/Windows/.test(ua)) {
    return 'Windows';
  }
  return '';
};

/**
 * Enrôlement d'une clé d'accès : options du core → `navigator.credentials.create()` (la fenêtre
 * système demande Face ID / Touch ID / Windows Hello) → vérification par le core. Réutilisé par
 * la page Sécurité et par la proposition faite juste après une connexion.
 */
export default function PasskeyEnrollForm({
  onCreated,
  compact = false,
}: {
  onCreated: (passkey: ApiPasskey) => void;
  /** Sans champ de nom : nom deviné, pour la proposition post-connexion. */
  compact?: boolean;
}) {
  const t = useTranslations('auth.passkey');
  const [deviceName, setDeviceName] = useState(guessDeviceName);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enroll = async () => {
    setPending(true);
    setError(null);
    try {
      const options = await passkeyRegisterOptionsAction();
      if (options.status === 'error') {
        setError(options.message);
        return;
      }
      let credential: unknown;
      try {
        credential = await createPasskey(options.data);
      } catch (e) {
        // Le trousseau détient déjà une clé Sowefund pour ce compte (le core envoie les clés
        // connues en `excludeCredentials`) : refus normal, une clé par compte et par trousseau.
        if (isPasskeyAlreadyRegistered(e)) {
          setError(t('alreadyRegistered'));
          return;
        }
        // Fenêtre fermée par l'utilisateur : pas une erreur.
        if (!isPasskeyCancelled(e)) {
          setError(t('enrollFailed'));
        }
        return;
      }
      const result = await passkeyRegisterVerifyAction(
        deviceName || t('defaultDeviceName'),
        credential,
      );
      if (result.status === 'error') {
        setError(result.message);
        return;
      }
      onCreated(result.data);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="passkey-enroll">
      {!compact ? (
        <div className="flex flex-col gap-1">
          <label
            htmlFor="passkey-device-name"
            className="text-sm font-semibold"
          >
            {t('deviceNameLabel')}
          </label>
          <input
            id="passkey-device-name"
            type="text"
            maxLength={128}
            value={deviceName}
            onChange={(event) => setDeviceName(event.target.value)}
            placeholder={t('deviceNamePlaceholder')}
            disabled={pending}
            className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      ) : null}
      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        type="button"
        onClick={enroll}
        disabled={pending}
        className="w-full sm:w-auto"
        data-testid="passkey-enroll-button"
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <KeyRound className="mr-1.5 h-4 w-4" strokeWidth={1.75} />
            {t('enroll')}
          </>
        )}
      </Button>
    </div>
  );
}
